import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import { PageLoader } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Send, 
  ArrowLeft, 
  User, 
  ChevronRight,
  Package
} from 'lucide-react';

export const MessagesPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Get conversation ID from URL if present
  const urlConversationId = searchParams.get('conversation');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchConversations();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (urlConversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === urlConversationId);
      if (conv) {
        selectConversation(conv);
      }
    }
  }, [urlConversationId, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await api().get('/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conversation) => {
    setActiveConversation(conversation);
    
    // Clear previous polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    try {
      const response = await api().get(`/conversations/${conversation.id}/messages`);
      setMessages(response.data);
      
      // Start polling for new messages
      pollIntervalRef.current = setInterval(async () => {
        try {
          const pollResponse = await api().get(`/conversations/${conversation.id}/messages`);
          setMessages(pollResponse.data);
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 5000); // Poll every 5 seconds
      
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation || sending) return;

    const otherParticipant = activeConversation.participants.find(p => p.id !== user.id);
    if (!otherParticipant) return;

    setSending(true);
    try {
      await api().post('/messages', {
        receiver_id: otherParticipant.id,
        content: newMessage.trim(),
        listing_id: activeConversation.listing_context?.id
      });
      
      setNewMessage('');
      
      // Refresh messages
      const response = await api().get(`/conversations/${activeConversation.id}/messages`);
      setMessages(response.data);
      
      // Update conversation list
      fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipant = (conversation) => {
    return conversation.participants.find(p => p.id !== user.id);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (!isAuthenticated) return null;
  if (loading) return <PageLoader />;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-20 h-screen flex flex-col">
        <div className="flex-1 max-w-6xl mx-auto w-full flex overflow-hidden">
          {/* Conversations List */}
          <div className={`w-full md:w-80 border-r border-white/5 flex flex-col ${activeConversation ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-white/5">
              <h1 className="font-heading font-bold text-xl text-white">Messages</h1>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No messages yet</p>
                  <p className="text-zinc-500 text-sm mt-2">
                    Start a conversation by messaging a seller
                  </p>
                </div>
              ) : (
                conversations.map((conv) => {
                  const other = getOtherParticipant(conv);
                  return (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full p-4 flex items-start gap-3 hover:bg-white/5 transition-colors text-left border-b border-white/5 ${
                        activeConversation?.id === conv.id ? 'bg-white/5' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                        {other?.profile_picture ? (
                          <img src={other.profile_picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-blue-400" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-white truncate">{other?.name}</p>
                          {conv.last_message && (
                            <span className="text-xs text-zinc-500">
                              {formatTime(conv.last_message.created_at)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400 truncate">@{other?.username}</p>
                        {conv.last_message && (
                          <p className="text-sm text-zinc-500 truncate mt-1">
                            {conv.last_message.sender_id === user.id ? 'You: ' : ''}
                            {conv.last_message.content}
                          </p>
                        )}
                        {conv.listing_context && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-zinc-500">
                            <Package className="w-3 h-3" />
                            <span className="truncate">{conv.listing_context.player_name}</span>
                          </div>
                        )}
                      </div>
                      
                      {conv.unread_count > 0 && (
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${activeConversation ? 'flex' : 'hidden md:flex'}`}>
            {activeConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-white/5 flex items-center gap-4">
                  <button
                    onClick={() => setActiveConversation(null)}
                    className="md:hidden p-2 hover:bg-white/5 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5 text-white" />
                  </button>
                  
                  <div 
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => {
                      const other = getOtherParticipant(activeConversation);
                      if (other) navigate(`/profile/${other.id}`);
                    }}
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">
                        {getOtherParticipant(activeConversation)?.name}
                      </p>
                      <p className="text-xs text-zinc-500">
                        @{getOtherParticipant(activeConversation)?.username}
                      </p>
                    </div>
                  </div>
                  
                  {activeConversation.listing_context && (
                    <button
                      onClick={() => navigate(`/cards/${activeConversation.listing_context.id}`)}
                      className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      {activeConversation.listing_context.image_url && (
                        <img 
                          src={activeConversation.listing_context.image_url} 
                          alt="" 
                          className="w-8 h-10 rounded object-cover"
                        />
                      )}
                      <div className="text-left">
                        <p className="text-xs text-zinc-400">About</p>
                        <p className="text-sm text-white truncate max-w-[100px]">
                          {activeConversation.listing_context.player_name}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                    </button>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === user.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                        data-testid={`message-${msg.id}`}
                      >
                        <div className={`max-w-[70%] ${isOwn ? 'order-1' : ''}`}>
                          <div
                            className={`px-4 py-3 rounded-2xl ${
                              isOwn 
                                ? 'bg-blue-600 text-white rounded-br-md' 
                                : 'bg-white/10 text-white rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                          </div>
                          <p className={`text-xs text-zinc-500 mt-1 ${isOwn ? 'text-right' : ''}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-white/5">
                  <div className="flex gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-white/5 border-white/10 focus:border-blue-500"
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="bg-blue-600 hover:bg-blue-500 px-6"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <MessageCircle className="w-16 h-16 text-zinc-600 mb-4" />
                <h2 className="text-xl font-medium text-white mb-2">Select a conversation</h2>
                <p className="text-zinc-500">Choose a conversation from the list to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
